
import React from 'react';
import { motion } from 'framer-motion';
// Added Check to the imports from lucide-react
import { ArrowLeft, Target, Shield, MessageCircle, ArrowRight, Zap, RefreshCw, Layers, Heart, Users, Calendar, Check } from 'lucide-react';
import { Language, View } from '../types';

interface ApproachPageProps {
  lang: Language;
  setView: (view: View) => void;
  onBack: () => void;
}

const ApproachPage: React.FC<ApproachPageProps> = ({ lang, setView, onBack }) => {
  const gradientTextClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]";
  const gradientBgClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 animate-gradient bg-[length:200%_auto]";

  const content = {
    title: { en: "My Coaching Approach", fa: "رویکرد کوچینگ من" },
    subtitle: { 
      en: "Deep inner work for sustainable life design", 
      fa: "کار درونی عمیق برای طراحی پایدار زندگی" 
    },
    intro: {
      en: "Most personal growth programs focus on behavior: habits, goal setting, productivity systems. My work is on the system that produces these behaviors—your internal operating system.\n\nTransformation isn't about 'fixing you'; it's about redesigning how your inner system decides, handles fear, creates meaning, and chooses direction.",
      fa: "بیشتر برنامه‌های رشد فردی روی رفتار تمرکز دارن: عادت‌ها، هدف‌گذاری، سیستم‌های بهره‌وری. کار من روی سیستمیه که این رفتارها رو تولید می‌کنه—سیستم عامل درونی تو.\n\nتو چارچوب من، تحول یعنی «درست کردن تو» نیست. بلکه بازطراحی شیوه‌ایه که سیستم درونی‌ات تصمیم می‌گیره، با ترس برخورد می‌کنه، معنا می‌سازه و جهت انتخاب می‌کنه."
    },
    pillarsHeading: {
      en: "This approach is built on three pillars:",
      fa: "این رویکرد سه ستون داره:"
    }
  };

  const Pillar = ({ icon: Icon, title, desc, children }: { icon: any, title: string, desc: string, children?: React.ReactNode }) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="p-8 md:p-12 bg-white border border-gray-100 rounded-sm hover:shadow-xl transition-all group"
    >
      <div className="flex items-center gap-6 mb-8">
        <div className="p-4 bg-teal-50 text-teal-500 rounded-full group-hover:bg-teal-500 group-hover:text-white transition-colors">
          <Icon size={32} />
        </div>
        <h3 className="text-2xl md:text-3xl font-black text-brandDark uppercase leading-tight">
          {title}
        </h3>
      </div>
      <p className="text-lg md:text-xl font-bold text-gray-500 mb-8 uppercase tracking-widest leading-tight">
        {desc}
      </p>
      <div className="prose prose-lg text-gray-500 font-medium leading-relaxed space-y-4">
        {children}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-white selection:bg-teal-500/20">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <button 
            onClick={onBack}
            className={`flex items-center gap-2 ${gradientTextClasses} font-black uppercase text-xs tracking-widest hover:translate-x-[-4px] transition-transform mb-8`}
          >
            <ArrowLeft size={16} className="text-teal-500" />
            {lang === 'en' ? "Back" : "بازگشت"}
          </button>
          
          <h1 className="hero-heading font-black text-brandDark uppercase leading-none mb-6">
            {content.title[lang]}
          </h1>
          <p className="text-xl md:text-2xl font-black text-gray-500 uppercase tracking-widest mb-10">
            {content.subtitle[lang]}
          </p>
          <div className="p-8 bg-gray-50 border-l-4 rtl:border-l-0 rtl:border-r-4 border-teal-500 rounded-sm">
            <p className="text-lg md:text-xl font-medium text-gray-600 leading-relaxed italic whitespace-pre-line">
              {content.intro[lang]}
            </p>
          </div>
        </motion.div>

        <div className="w-full flex flex-col items-center py-8">
           <h2 className="text-brandDark font-black uppercase tracking-widest text-lg mb-6">
              {content.pillarsHeading[lang]}
           </h2>
           <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center gap-4"
           >
              <ArrowDown className="text-teal-500 w-12 h-12" />
           </motion.div>
        </div>

        <div className="space-y-24">
          
          {/* Pillar 1: Systems Thinking */}
          <Pillar 
            icon={RefreshCw} 
            title={lang === 'en' ? "Pillar One: Systems Thinking" : "ستون اول: تفکر سیستمی"}
            desc={lang === 'en' 
              ? "Everything inside us works perfectly towards its goals. Even in our worst moments!" 
              : "درون همه‌ی ما در جهت اهدافش داره بی‌نقص کار می‌کنه. حتی در بدترین شرایطمون!"}
          >
            {lang === 'en' ? (
              <>
                <p>Our core premise is that what happens inside you isn't random or "broken." The inner system—a mix of beliefs, fears, values, and survival strategies—is optimized for specific goals, often unconscious ones.</p>
                <div className="my-8 p-6 bg-brandDark text-white rounded flex items-center gap-6 relative overflow-hidden">
                  <div className="absolute right-0 top-0 opacity-10"><Zap size={100} /></div>
                  <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 w-full">
                    <div className="text-center p-4 border border-white/20 rounded">Old Strategy: Safety</div>
                    <ArrowRight className="hidden md:block" />
                    <div className="text-center p-4 border border-teal-500/50 bg-teal-500/10 rounded">Desired Goal: Growth</div>
                  </div>
                </div>
                <p>If you procrastinate or repeat patterns in relationships, your system is doing what was once useful: reducing risk, maintaining belonging, or avoiding shame. Pushing yourself or self-blame only increases systemic resistance. Coaching helps us understand this hidden logic and gradually retune the system for the goals you consciously choose today.</p>
              </>
            ) : (
              <>
                <p>فرض پایه‌ی ما اینه که چیزهایی که تو درونت اتفاق می‌افته تصادفی یا «معیوب» نیست. سیستم درونی—ترکیبی از باورها، ترس‌ها، ارزش‌ها و استراتژی‌های بقا—برای یه سری هدف مشخص بهینه شده؛ که البته خیلی وقت‌ها این اهداف آگاهانه انتخاب نشدن.</p>
                <div className="my-8 p-6 bg-brandDark text-white rounded flex items-center gap-6 relative overflow-hidden">
                  <div className="absolute left-0 top-0 opacity-10"><Zap size={100} /></div>
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 w-full" dir="ltr">
                    <div className="text-center p-4 border border-white/20 rounded w-full md:w-auto">استراتژی قدیمی: امنیت</div>
                    <ArrowRight className="hidden md:block" />
                    <div className="text-center p-4 border border-teal-500/50 bg-teal-500/10 rounded w-full md:w-auto">هدف جدید: پیشرفت</div>
                  </div>
                </div>
                <p>اگه امروز اهمال‌کاری می‌کنی، سیستمِ تو داره کاری رو می‌کنه که یه زمانی مفید بوده: کاهش ریسک، حفظ تعلق، یا جلوگیری از شرم. خودسرزنشی مقاومت سیستم رو بیشتر می‌کنه. کوچینگ یعنی منطق پنهان سیستم رو درک کنیم و کمک کنیم سیستم رو به‌تدریج برای اهدافی که امروز آگاهانه انتخاب می‌کنی بازتنظیم کنی.</p>
              </>
            )}
          </Pillar>

          {/* Pillar 2: IFS */}
          <Pillar 
            icon={Shield} 
            title={lang === 'en' ? "Pillar Two: IFS – Internal Family Systems" : "ستون دوم: IFS – سیستم خانواده‌ی درونی"}
            desc={lang === 'en' 
              ? "Compassion and negotiation with the inner system, not fighting it." 
              : "همدلی و مذاکره با سیستم درونی، نه جنگیدن باهاش"}
          >
            {lang === 'en' ? (
              <>
                <p>IFS is a modern psychological framework that sees the mind as a system of parts, not a single 'me.' Those voices in your head arguing with each other? They are complete personalities with specific protective goals. Without understanding them, they won't go quiet.</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <Layers className="text-teal-500 shrink-0" /> Identifying Key Parts
                  </li>
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <Heart className="text-teal-500 shrink-0" /> Turning Anger to Self-Love
                  </li>
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <RefreshCw className="text-teal-500 shrink-0" /> Resolving Inner Conflicts
                  </li>
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <Users className="text-teal-500 shrink-0" /> Building Self-Leadership
                  </li>
                </ul>
                <p>Most people try to change through willpower and shame. IFS creates change through understanding, alignment, and internal trust—leading to sustainable transformation.</p>
              </>
            ) : (
              <>
                <p>سیستم‌های خانواده درونی (IFS) یه چارچوب روان‌شناختی مدرنه که ذهن رو به‌عنوان یه سیستم از بخش‌ها می‌بینه، نه یه مَنِ واحد. دیدی گاهی صداهایی در درون ذهنت باهات حرف می‌زنن یا با همدیگه بحث می‌کنن؟ اینها هر کدوم یک شخصیت کامل هستن با اهداف مشخص. بدون فهمیدنشون ساکت نمی‌شن.</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <Layers className="text-teal-500 shrink-0" /> شناسایی بخش‌های کلیدی درونی
                  </li>
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <Heart className="text-teal-500 shrink-0" /> تبدیل سرزنش به شفقت
                  </li>
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <RefreshCw className="text-teal-500 shrink-0" /> کاهش جنگ‌های درونی
                  </li>
                  <li className="p-4 border border-gray-100 rounded-sm font-bold flex items-center gap-3">
                    <Users className="text-teal-500 shrink-0" /> ساخت رهبری درونی (Self)
                  </li>
                </ul>
                <p>بیشتر آدم‌ها سعی می‌کنن با زور اراده و شرم تغییر کنن. IFS تغییر رو از طریق فهم، هم‌راستایی و اعتماد درونی ایجاد می‌کنه—که به تحول پایدار می‌رسه.</p>
              </>
            )}
          </Pillar>

          {/* Pillar 3: NVC */}
          <Pillar 
            icon={MessageCircle} 
            title={lang === 'en' ? "Pillar Three: NVC – Nonviolent Communication" : "ستون سوم: ارتباط بدون خشونت (NVC)"}
            desc={lang === 'en' 
              ? "Deep connection with ourselves and those around us." 
              : "ارتباط عمیق با خودمون و اطرافیانمون"}
          >
            {lang === 'en' ? (
              <>
                <p>NVC is a communication tool that turns conflict into understanding. It helps us see the human needs and feelings hidden behind the words we say and hear. Even the harshest words come from understandable human needs.</p>
                <div className="p-6 bg-teal-50 rounded-sm my-6 border-r-4 border-teal-500">
                  <p className="font-black text-teal-900 mb-2 uppercase text-xs tracking-widest">NVC Utility:</p>
                  <p className="font-bold text-brandDark">Clarity on what we want + Expressing difficult boundaries + Hearing others without reactive anger = Relationships based on empathy.</p>
                </div>
                <p>Transformation fails when relationships collapse. NVC helps your growth integrate into your real relationships and real life.</p>
              </>
            ) : (
              <>
                <p>NVC یه ابزار ارتباطیه که تعارض رو به تفاهم تبدیل می‌کنه؛ چارچوبیه که کمک می‌کنه بفهمیم پشت حرفهایی که می‌زنیم چه نیازها و احساسات انسانی‌ای نهفته‌س. حتی شدید‌ترین حرفها هم از نیازهای انسانی‌ای میان که برای همه‌مون قابل درکه.</p>
                <div className="p-6 bg-teal-50 rounded-sm my-6 border-r-4 border-teal-500">
                  <p className="font-black text-teal-900 mb-2 uppercase text-xs tracking-widest">کاربرد NVC:</p>
                  <p className="font-bold text-brandDark">شفافیت خواسته‌ها + بیان راحت مرزهای سخت + شنیدن بقیه بدون عصبانیت = روابط مبتنی بر همدلی.</p>
                </div>
                <p>تحول وقتی شکست می‌خوره که روابط فرو می‌پاشن. NVC کمک می‌کنه رشد تو در روابط واقعی و زندگی واقعی ادغام بشه.</p>
              </>
            )}
          </Pillar>

          {/* Integration */}
          <section className="bg-brandDark text-white p-10 md:p-16 rounded-sm relative overflow-hidden">
             <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/20 blur-[100px] rounded-full" />
             <div className="relative z-10 space-y-8">
                <h2 className="text-3xl md:text-4xl font-black uppercase leading-tight">
                  {lang === 'en' ? "Integration of Frameworks" : "یکپارچه‌سازی این چارچوب‌ها"}
                </h2>
                <div className="space-y-6 text-lg md:text-xl text-gray-300 font-medium">
                   <p>{lang === 'en' ? "This approach offers a coherent model for inner redesign:" : "این رویکرد یه مدل منسجمه برای بازطراحی سیستم درونی بهمون می‌ده:"}</p>
                   <div className="grid md:grid-cols-3 gap-6 pt-4">
                      <div className="p-6 border border-white/10 rounded bg-white/5">
                        <p className="font-black text-white mb-2 uppercase text-sm">System Thinking</p>
                        <p className="text-sm">{lang === 'en' ? "Insight for growth without judgment." : "بینش شناخت به جای قضاوت."}</p>
                      </div>
                      <div className="p-6 border border-white/10 rounded bg-white/5">
                        <p className="font-black text-white mb-2 uppercase text-sm">IFS</p>
                        <p className="text-sm">{lang === 'en' ? "Turning conflict into self-love." : "تبدیل تضاد به همدلی با خود."}</p>
                      </div>
                      <div className="p-6 border border-white/10 rounded bg-white/5">
                        <p className="font-black text-white mb-2 uppercase text-sm">NVC</p>
                        <p className="text-sm">{lang === 'en' ? "Relating with the world with clarity." : "ارتباط با جهان با شفافیت."}</p>
                      </div>
                   </div>
                   <p className="text-white font-black italic pt-6">
                     {lang === 'en' 
                       ? "Together, these three layers take you from trying to fix behavior with pressure, to conscious systemic design with self-love."
                       : "کنار هم، این سه لایه تو رو از تلاش برای اصلاح رفتار با فشار، به طراحی آگاهانه‌ی سیستمی با عشق به خود می‌بره."}
                   </p>
                </div>
             </div>
          </section>

          {/* Role of Coach */}
          <section className="mb-20">
             <h2 className="text-2xl md:text-3xl font-black text-brandDark uppercase mb-8">
               {lang === 'en' ? "The Role of the Coach" : "نقش کوچ در این مدل"}
             </h2>
             <p className="text-lg text-gray-500 font-medium mb-10">
               {lang === 'en' 
                 ? "I am not your consultant, therapist, or motivational speaker. My role is:" 
                 : "من مشاور، درمانگر یا سخنران انگیزشی تو نیستم. نقش من:"}
             </p>
             <div className="grid md:grid-cols-2 gap-6">
                {[
                  { en: "Strategic partner in redesigning your inner system", fa: "شریک استراتژیک در بازطراحی سیستم درونی‌ات" },
                  { en: "A mirror for blind spots and unconscious patterns", fa: "آینه‌ای برای نقاط کور و الگوهای ناخودآگاه" },
                  { en: "A safe space for high-stakes inner exploration", fa: "یه فضای امن برای کاوش درونی با ریسک بالا" },
                  { en: "Accountability structure for aligned action", fa: "ساختار مسئولیت‌پذیری برای اقدام هم‌راستا" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-5 bg-gray-50 rounded-sm border border-gray-100">
                     <Check className="w-5 h-5 text-teal-500 mt-1 shrink-0" />
                     <span className="text-brandDark font-bold">{item[lang]}</span>
                  </div>
                ))}
             </div>
          </section>

          {/* Outcome */}
          <section className="mb-20">
             <h2 className="text-2xl md:text-3xl font-black text-brandDark uppercase mb-8">
               {lang === 'en' ? "What This Approach Makes Possible" : "این رویکرد چی رو ممکن می‌کنه"}
             </h2>
             <div className="space-y-4">
                {[
                  { en: "Cohesive inner leadership instead of inner conflict", fa: "رهبری درونی منسجم به‌جای تعارض درونی" },
                  { en: "Clearer orientation and less existential confusion", fa: "جهت‌گیری شفاف‌تر و سردرگمی وجودی کمتر" },
                  { en: "Self-empathy and reduced internal friction", fa: "همدلی و عشق به خود و کم شدن تعارض‌های درونی" },
                  { en: "Actions that feel aligned, not forced", fa: "اقدام‌هایی که هم‌راستا احساس می‌شن، نه تحمیلی" },
                  { en: "A real sense of agency over your life path", fa: "حس واقعی عاملیت روی مسیر زندگی" }
                ].map((item, i) => (
                   <div key={i} className="flex gap-4 items-center">
                      <div className="w-2 h-2 rounded-full bg-teal-500" />
                      <p className="text-gray-600 font-medium text-lg">{item[lang]}</p>
                   </div>
                ))}
             </div>
          </section>

          {/* Is it for you? */}
          <section className="mb-20 p-8 md:p-12 border border-gray-100 rounded-sm">
             <h2 className="text-2xl md:text-3xl font-black text-brandDark uppercase mb-8">
               {lang === 'en' ? "Is This For You?" : "آیا این رویکرد برای تو مناسبه؟"}
             </h2>
             <div className="space-y-4">
               {[
                 { en: "You want transformation at the identity level, not just surface changes", fa: "تغییر در سطح هویت می‌خوان، نه تغییرات سطحی" },
                 { en: "You're willing to honestly explore your inner world", fa: "حاضرند جهان درونی‌شون رو صادقانه کاوش کنن" },
                 { en: "You prefer framework and clarity over spiritual bypassing", fa: "چارچوب و وضوح رو به دور زدن معنوی ترجیح می‌دن" },
                 { en: "You're building a life, career, or relationship aligned with deep values", fa: "دارن زندگی، حرفه یا رابطه‌ای هم‌راستا با ارزش‌های عمیق می‌سازن" }
               ].map((item, i) => (
                 <div key={i} className="flex items-center gap-4 text-gray-500">
                    <Check className="w-5 h-5 text-gray-300" />
                    <span className="font-medium">{item[lang]}</span>
                 </div>
               ))}
             </div>
          </section>

          {/* CTA Final */}
          <section className="pb-20">
            <div className="bg-gray-50 p-10 md:p-16 rounded-sm text-center border border-gray-200">
               <div className="space-y-8">
                 <h2 className="text-3xl md:text-5xl font-black uppercase leading-none text-brandDark">
                   {lang === 'en' ? "Next Step" : "گام بعدی"}
                 </h2>
                 <p className="text-gray-500 max-w-xl mx-auto text-lg font-medium whitespace-pre-line">
                   {lang === 'en' 
                     ? "An honest chat. To answer your questions and explore your path.\nFree, 30-60 mins, no obligation." 
                     : "یک گپ صادقانه. برای برطرف کردن سوالات تو و کشف مسیر تو.\nرایگان، ۳۰-۶۰ دقیقه، بدون تعهد"}
                 </p>
                 <div className="flex flex-col md:flex-row gap-4 justify-center pt-4">
                   <a 
                     href="https://calendar.app.google/NbuyxdkKnzVU9gj17" 
                     target="_blank"
                     rel="noopener noreferrer"
                     className={`inline-flex items-center justify-center gap-3 ${gradientBgClasses} text-white hover:opacity-90 px-8 py-4 text-lg font-black uppercase tracking-widest transition-all shadow-xl hover:shadow-2xl rounded-full`}
                   >
                     <Calendar className="w-5 h-5" />
                     {lang === 'en' ? "Request Discovery Session" : "درخواست جلسه‌ی آشنایی"}
                   </a>
                 </div>
               </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

// Simple ArrowDown component replacement if not imported
const ArrowDown = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M12 5v14M19 12l-7 7-7-7"/>
  </svg>
);

export default ApproachPage;
